/**
 * KPI tile: pale tinted ground, label and dot left, big number right.
 *
 * Lifted from the portal's metric-tile pattern
 * (`widgets/user-security/mfa-compliance.tsx`), which pairs a pastel
 * surface with saturated text of the matching hue.
 */

import clsx from 'clsx';
import type React from 'react';
import type { ReactNode } from 'react';

import { colors } from './tokens.js';

export interface MetricTileProps {
  label: string;
  value: number | string;
  /** Accent for the dot and the number. */
  color?: string;
  /** Pale ground. Defaults to the neutral card surface. */
  tint?: string;
  /** One muted line under the label: the qualifier the number needs. */
  sub?: ReactNode;
  onClick?: () => void;
  className?: string;
}

export function MetricTile({
  label,
  value,
  color = '#2A3241',
  tint = '#FFFFFF',
  sub,
  onClick,
  className,
}: MetricTileProps) {
  // A clickable tile is a link in all but markup: reachable by keyboard,
  // activated by Enter or Space, announced as a button.
  const interactive = onClick
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick,
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        },
      }
    : {};
  return (
    <div
      {...interactive}
      className={clsx(
        'flex min-w-[130px] flex-1 items-center justify-between gap-3 rounded-card px-4 py-3',
        onClick &&
          'cursor-pointer transition-transform hover:scale-[1.02] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[#76B7CD]',
        className,
      )}
      style={{ backgroundColor: tint }}
    >
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-sm" style={{ color }}>
          <span
            aria-hidden="true"
            className="inline-block h-3 w-3 shrink-0 rounded-full"
            style={{ backgroundColor: color }}
          />
          {label}
        </p>
        {sub && (
          <p className="mt-0.5 truncate text-xs" style={{ color: colors.muted }}>
            {sub}
          </p>
        )}
      </div>
      <p className="shrink-0 text-2xl font-bold leading-none tabular-nums" style={{ color }}>
        {value}
      </p>
    </div>
  );
}
